/**
 * A RUN'S OUTCOME — ONE DEFINITION, READ BY BOTH SIDES (§1, 2026-09-22).
 *
 * ⚠️⚠️ A LEAF (types only), because the SERVER derives `diedRunCount` for the
 * job's health and the CLIENT derives the chip on the run table, and those two
 * must not be able to disagree about whether a run died. They would: the page's
 * own `runStatus` read `failedCount`/`succeededCount` alone, so a start record
 * with no counts rendered as "Succeeded 0 / 0 / 0" — a killed run wearing a
 * green chip, which is worse than the blank row it replaced.
 *
 * PURE: `now` is an argument everywhere.
 */
/**
 * ⚠️⚠️ FOUR STATES, AND THE FOURTH IS THE ONE THIS EXISTS FOR (2026-09-22).
 *
 *     succeeded  finished, nothing failed inside it
 *     partial    finished, some accounts failed inside it
 *     running    started, no finish yet, and not yet past the point where Cloud
 *                Run must have killed it
 *     died       started, no finish, and past that point
 *
 * ⚠️ `died` WAS NOT EXPRESSIBLE BEFORE THE START RECORD. The run row was written
 * on COMPLETION, so a killed run wrote nothing at all and the page had no row —
 * which renders as "no run happened". Three SIGSEGV deaths in fifteen runs on
 * 2026-09-22 showed up as a six-hour scheduler outage that never occurred.
 *
 * ⚠️⚠️ AND THE ABSENCE NOW MEANS SOMETHING TOO, which is the other half of the
 * win: with start records, NO ROW for a window the scheduler should have fired
 * in genuinely means the job never started — the scheduler did not fire, or
 * Cloud Run refused it. Three causes, one appearance, now separable.
 */
export type ReportingRunOutcome = 'succeeded' | 'partial' | 'running' | 'died';

/**
 * ⚠️⚠️ THE JOB'S OWN `--task-timeout`, IN MINUTES — an OBSERVATION, not a guess.
 * `deploy:reporting-job` passes `--task-timeout=7200`, so Cloud Run has certainly
 * killed the task by then: past this point a run with no recorded finish cannot
 * still be running.
 *
 * ⚠️ Deliberately NOT twice the timeout (the CRM `presumed_failed` rule), because
 * that rule is compensating for having no start record at all. Here we KNOW the
 * run started, and the platform's own ceiling is the honest bound.
 *
 * ⚠️ IT IS STILL CONSERVATIVE IN PRACTICE: measured 2026-09-22, healthy runs take
 * 11m47s–17m39s and the three that died lasted 11m40s and 17m03s. So a died run
 * reads `running` for up to two hours before flipping — and on a 2-hourly
 * schedule the next run usually starts first. THAT is why the run LIST, not the
 * single latest-run verdict, is the thing that had to show this.
 */
export const RUN_TASK_TIMEOUT_MINUTES = 120;

/**
 * One run's outcome. PURE — `now` is an argument.
 *
 * ⚠️ `partial` READS `failedCount`, WHICH IS ABOUT ACCOUNTS, NOT THE JOB. A run
 * that finished having failed 39 of 234 accounts DID run; the accounts are the
 * worklist's business. It is amber here rather than red for exactly that reason,
 * and `toMetaJobHealth` deliberately does not let it colour the JOB row.
 */
export function runOutcome(
  run: { startedAt: string; finishedAt: string | null; failedCount: number },
  nowMs: number,
): ReportingRunOutcome {
  if (run.finishedAt !== null) return run.failedCount > 0 ? 'partial' : 'succeeded';
  const started = Date.parse(run.startedAt);
  // ⚠️ An unreadable start time reads as `running`, never `died`. Calling a run
  // dead on a parse failure is an accusation made from missing evidence, and the
  // direction matters: a wrongly-running row is quiet, a wrongly-died row sends
  // someone to investigate a run that is fine.
  if (Number.isNaN(started)) return 'running';
  return (nowMs - started) / 60_000 > RUN_TASK_TIMEOUT_MINUTES ? 'died' : 'running';
}

/**
 * How many of the given runs died. ⚠️ The count is what makes a 20% death rate
 * visible: the LATEST run is usually a healthy one (the next scheduled run
 * supersedes a corpse within two hours), so a job judged only on its last run
 * reads green through exactly the failure this brief is about.
 */
export function diedRunCount(
  runs: readonly { startedAt: string; finishedAt: string | null; failedCount: number }[],
  nowMs: number,
): number {
  return runs.filter((r) => runOutcome(r, nowMs) === 'died').length;
}
